import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';

import { UISelectableOptionsCard } from '@futrli/ui-components';

import { CoaStructureTypes } from '../../../core/types';
import { useConsolidationContext } from '../../consolidation-context/consolidation-context';
import styles from './coa-structure.scss';

export const CoaStructure = () => {
  const intl = useIntl();
  const {
    coaStructure: { coaStructure, handleChangeCoaStructure }
  } = useConsolidationContext();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleChangeCoaStructure(event.target.value as CoaStructureTypes);
    },
    [handleChangeCoaStructure]
  );

  const sharedInputProps = {
    inputType: 'radio' as React.ComponentProps<typeof UISelectableOptionsCard>['inputType'],
    inputName: 'coaStructure',
    onChange: handleChange
  };

  return (
    <div>
      <p className={styles.description}>
        <FormattedMessage
          id="consolidation.coa_structure_for_reporting"
          defaultMessage="Chart of accounts structure for reporting, and consolidated forecast"
        />
      </p>

      <ul className={styles.options}>
        <li>
          <UISelectableOptionsCard
            title={intl.formatMessage({
              id: 'consolidation.coa_structure_group_by_org_and_custom_groups',
              defaultMessage: 'Group by organisation and custom groups'
            })}
            {...sharedInputProps}
            inputValue={CoaStructureTypes.GROUP_BY_ORG_AND_CUSTOM_GROUPS}
            checked={coaStructure === CoaStructureTypes.GROUP_BY_ORG_AND_CUSTOM_GROUPS}
          >
            <span className={clsx(styles.text, styles.heading)}>
              {intl.formatMessage({
                id: 'consolidation.income_account_category',
                defaultMessage: 'Income (account category)'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.organisation_a',
                defaultMessage: 'Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.subheading, styles.indent2)}>
              {intl.formatMessage({
                id: 'consolidation.group_name',
                defaultMessage: 'Group name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.organisation_b',
                defaultMessage: 'Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.subheading, styles.indent2)}>
              {intl.formatMessage({
                id: 'consolidation.group_name',
                defaultMessage: 'Group name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
          </UISelectableOptionsCard>
        </li>
        <li>
          <UISelectableOptionsCard
            title={intl.formatMessage({
              id: 'consolidation.coa_structure_group_by_org_and_discard_custom_groups',
              defaultMessage: 'Group by organisation and discard custom groups'
            })}
            {...sharedInputProps}
            inputValue={CoaStructureTypes.GROUP_BY_ORG_AND_DISCARD_CUSTOM_GROUPS}
            checked={coaStructure === CoaStructureTypes.GROUP_BY_ORG_AND_DISCARD_CUSTOM_GROUPS}
          >
            <span className={clsx(styles.text, styles.heading)}>
              {intl.formatMessage({
                id: 'consolidation.income_account_category',
                defaultMessage: 'Income (account category)'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.organisation_a',
                defaultMessage: 'Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.organisation_b',
                defaultMessage: 'Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.organisation_c',
                defaultMessage: 'Organisation C'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name',
                defaultMessage: 'Account name'
              })}
            </span>
          </UISelectableOptionsCard>
        </li>

        <li>
          <UISelectableOptionsCard
            title={intl.formatMessage({
              id: 'consolidation.coa_structure_group_by_any_identical_name',
              defaultMessage: 'Group by any identical name'
            })}
            {...sharedInputProps}
            inputValue={CoaStructureTypes.GROUP_BY_ANY_IDENTICAL_NAME}
            checked={coaStructure === CoaStructureTypes.GROUP_BY_ANY_IDENTICAL_NAME}
          >
            <span className={clsx(styles.text, styles.heading)}>
              {intl.formatMessage({
                id: 'consolidation.income_account_category',
                defaultMessage: 'Income (account category)'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a',
                defaultMessage: 'Account name A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_a',
                defaultMessage: 'Account name A: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_b',
                defaultMessage: 'Account name A: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b',
                defaultMessage: 'Account name B'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_a',
                defaultMessage: 'Account name B: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_b',
                defaultMessage: 'Account name B: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_c',
                defaultMessage: 'Account name C'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_c_organisation_a',
                defaultMessage: 'Account name C: Organisation A'
              })}
            </span>
          </UISelectableOptionsCard>
        </li>

        <li>
          <UISelectableOptionsCard
            title={intl.formatMessage({
              id: 'consolidation.coa_structure_choice_flatten_coa',
              defaultMessage: 'Flatten chart of accounts (no custom groupings)'
            })}
            {...sharedInputProps}
            inputValue={CoaStructureTypes.FLATTEN_COA}
            checked={coaStructure === CoaStructureTypes.FLATTEN_COA}
          >
            <span className={clsx(styles.text, styles.heading)}>
              {intl.formatMessage({
                id: 'consolidation.income_account_category',
                defaultMessage: 'Income (account category)'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_a',
                defaultMessage: 'Account name A: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_b',
                defaultMessage: 'Account name A: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_a',
                defaultMessage: 'Account name B: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_b',
                defaultMessage: 'Account name B: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_c_organisation_a',
                defaultMessage: 'Account name C: Organisation A'
              })}
            </span>
          </UISelectableOptionsCard>
        </li>
        <li>
          <UISelectableOptionsCard
            title={intl.formatMessage({
              id: 'consolidation.coa_structure_group_by_any_identical_custom_group_name',
              defaultMessage: 'Group by any identical custom group name'
            })}
            {...sharedInputProps}
            inputValue={CoaStructureTypes.GROUP_BY_ANY_IDENICAL_CUSTOM_GROUP_NAME}
            checked={coaStructure === CoaStructureTypes.GROUP_BY_ANY_IDENICAL_CUSTOM_GROUP_NAME}
          >
            <span className={clsx(styles.text, styles.heading)}>
              {intl.formatMessage({
                id: 'consolidation.income_account_category',
                defaultMessage: 'Income (account category)'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.group_name',
                defaultMessage: 'Group name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_a',
                defaultMessage: 'Account name A: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_a',
                defaultMessage: 'Account name B: Organisation A'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_a_organisation_b',
                defaultMessage: 'Account name A: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_b_organisation_b',
                defaultMessage: 'Account name B: Organisation B'
              })}
            </span>
            <span className={clsx(styles.text, styles.heading, styles.indent1)}>
              {intl.formatMessage({
                id: 'consolidation.group_name',
                defaultMessage: 'Group name'
              })}
            </span>
            <span className={clsx(styles.text, styles.indent3)}>
              {intl.formatMessage({
                id: 'consolidation.account_name_c_organisation_a',
                defaultMessage: 'Account name C: Organisation A'
              })}
            </span>
          </UISelectableOptionsCard>
        </li>
      </ul>
    </div>
  );
};
